import { ClientLoggingService } from './client-logging.service';
import { Injectable, inject } from '@angular/core';
import { EnvironmentService } from './environment.service';
import { AuthInterceptor } from './auth.interceptor';
import { ArchitecturalPlanWriteServiceClient } from '../../generated.commonjs/ApiServiceClientPb';
import {
  RotateArchitecturalPlanPageRequest,
  RotateArchitecturalPlanPageResponse,
} from '../../generated.commonjs/api_pb';

export interface PageRotationResult {
  success: boolean;
  message: string;
  /** The page's clockwise rotation before the request (Issue #2445). */
  previousRotation: number;
  /** The page's clockwise rotation now. */
  effectiveRotation: number;
  /** Grows with every committed rotation of the page; a change means cached geometry is stale. */
  geometryRevision: number;
  /** True when the page was already at the requested rotation and nothing was changed. */
  alreadyApplied: boolean;
}

/**
 * The server refused to turn the page because its state did not match the request (Issue #2445):
 * either another rotation is in progress, or the page is not at the rotation this client was
 * showing. Nothing was changed. The caller should reload the page and let the user try again.
 */
export class PageRotationConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PageRotationConflictError';
  }
}

/** gRPC status code the server uses for a rotation it refused. */
const GRPC_ABORTED = 10;

/** Handles page rotation operations via the ArchitecturalPlanWriteService gRPC endpoint. */
@Injectable({
  providedIn: 'root',
})
export class PageRotationService {
  private environmentService = inject(EnvironmentService);
  private authInterceptor = inject(AuthInterceptor);
  private client: ArchitecturalPlanWriteServiceClient;

  constructor() {
    const apiServer = this.environmentService.getApiServer();
    const options = {
      unaryInterceptors: [this.authInterceptor],
    };
    this.client = new ArchitecturalPlanWriteServiceClient(`https://${apiServer}:443`, null, options);
  }

  /**
   * Rotates a page PDF by the specified degrees.
   * @param projectId The architectural plan project ID
   * @param pageNumber The page number (1-based)
   * @param degrees Rotation degrees clockwise: 90, 180, or 270
   * @param fileId Optional file ID for multi-file projects
   * @param expectedRotation The rotation the page is displayed at here. The server uses it to
   *     recognise a repeated request, which it answers without turning the page a second time, and
   *     to refuse a request made against an out-of-date view.
   * @throws PageRotationConflictError when the server refuses the request and changes nothing
   */
  async rotatePage(
    projectId: string,
    pageNumber: number,
    degrees: number,
    fileId?: string,
    expectedRotation?: number
  ): Promise<PageRotationResult> {
    ClientLoggingService.log('INFO', `[PageRotationService] Rotating page ${pageNumber} by ${degrees}°`, {
      projectId,
      pageNumber,
      degrees,
      fileId,
    });

    const request = new RotateArchitecturalPlanPageRequest();
    request.setProjectId(projectId);
    request.setPageNumber(pageNumber);
    request.setDegrees(degrees);
    if (fileId) {
      request.setFileId(fileId);
    }
    if (expectedRotation !== undefined) {
      request.setExpectedRotationDegrees(expectedRotation);
    }

    try {
      const response: RotateArchitecturalPlanPageResponse = await this.client.rotateArchitecturalPlanPage(
        request,
        null
      );

      ClientLoggingService.log('INFO', '[PageRotationService] Rotation response:', {
        success: response.getSuccess(),
        message: response.getMessage(),
      });

      return {
        success: response.getSuccess(),
        message: response.getMessage(),
        previousRotation: response.getPreviousRotationDegrees(),
        effectiveRotation: response.getEffectiveRotationDegrees(),
        geometryRevision: response.getGeometryRevision(),
        alreadyApplied: response.getAlreadyApplied(),
      };
    } catch (error: any) {
      ClientLoggingService.log('ERROR', '[PageRotationService] Rotation failed:', error);
      if (error?.code === GRPC_ABORTED) {
        throw new PageRotationConflictError(error?.message ?? 'The page could not be rotated right now.');
      }
      throw error;
    }
  }
}
